import React from 'react';
import {
  Box,
  Button,
  Icons,
  IconButton,
  Modal,
  Tooltip,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';

type DeleteAssignmentModalProps = {
  open: boolean;
  assignmentTitle: string;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
};

const DeleteAssignmentModal: React.FC<DeleteAssignmentModalProps> = ({
  open,
  loading = false,
  onClose,
  onConfirm,
}) => {
  const theme = useTheme();
  const { palette } = theme;
  const styles = {
    dialog: {
      width: { xs: 343, md: 560 },
      bgcolor: 'background.paper',
      borderRadius: '8px',
      boxSizing: 'border-box',
      p: '1.5rem',
      outline: 'none',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem',
      mb: { xs: '12px', sm: '16px' },
      minHeight: { xs: '44px', sm: '40px' },
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.6,
      letterSpacing: '0.15px',
      color: palette?.text?.primary,
      py: '0.25rem',
    },
    closeButton: {
      color: palette?.grey?.[500] ?? palette?.text?.secondary,
      flexShrink: 0,
      mr: '-0.25rem',
    },
    body: {
      fontFamily: theme?.typography?.fontFamily,
      fontWeight: theme?.typography?.fontWeightRegular ?? 400,
      fontStyle: 'normal',
      fontSize: '1rem',
      color: palette?.text?.primary,
      mb: '1.5rem',
      lineHeight: '180%',
      letterSpacing: '0.15px',
    },
    footer: {
      display: 'flex',
      gap: '0.75rem',
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
  };

  const buttonSx = {
    width: { xs: 'auto', sm: 'auto' },
    minWidth: '88px',
    height: '42px',
    minHeight: '42px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: palette?.primary?.dark,
      borderColor: palette?.primary?.dark,
    },
    '&.MuiButton-outlinedPrimary:hover': {
      backgroundColor: 'transparent',
      borderColor: palette?.primary?.main,
    },
  };

  return (
    <Modal open={open} onClose={loading ? undefined : onClose}>
      <Box sx={styles.dialog}>
        <Box sx={styles.header}>
          <Typography
            sx={styles.title}
            id="delete-assignment-title"
            tabIndex={0}
          >
            Delete this assignment?
          </Typography>
          <IconButton
            aria-label="Close delete assignment dialog"
            onClick={onClose}
            sx={styles.closeButton}
            disabled={loading}
          >
            <Tooltip title="Close">
              <Icons.Close />
            </Tooltip>
          </IconButton>
        </Box>

        <Typography component="div" sx={styles.body} tabIndex={0}>
          Are you sure that you want to delete this assignment?{' '}
        </Typography>

        <Box sx={styles.footer}>
          <Button
            kind="secondary"
            type="button"
            sx={buttonSx}
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            kind="primary"
            type="button"
            sx={buttonSx}
            onClick={onConfirm}
            disabled={loading}
            loading={loading}
          >
            Delete
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default DeleteAssignmentModal;
